/**
 * TestCase Entity - Handles test case data operations
 * Simulates database operations with localStorage for this demo
 */

const STORAGE_KEY = 'ai_eval_test_cases';

export class TestCase {
  constructor(data = {}) {
    this.id = data.id || crypto.randomUUID();
    this.title = data.title || '';
    this.user_input = data.user_input || '';
    this.expected_response_answer = data.expected_response_answer || '';
    this.expected_response_question = data.expected_response_question || '';
    this.actual_response_answer = data.actual_response_answer || '';
    this.actual_response_question = data.actual_response_question || '';
    this.config_data = data.config_data || {};
    this.preset_type = data.preset_type || 'custom';
    this.environment = data.environment || 'dev';
    this.created_date = data.created_date || new Date().toISOString();
    this.updated_date = data.updated_date || new Date().toISOString();
  }

  /**
   * Create a new test case
   * @param {Object} data - Test case data
   * @returns {Promise<TestCase>} - Created test case
   */
  static async create(data) {
    const testCase = new TestCase(data);
    const testCases = await TestCase.list();
    testCases.push(testCase);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testCases));
    return testCase;
  }

  /**
   * List all test cases
   * @param {string} orderBy - Field to order by (default: 'created_date')
   * @returns {Promise<TestCase[]>} - Array of test cases
   */
  static async list(orderBy = 'created_date') {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const testCases = stored ? JSON.parse(stored) : [];
      
      // Convert plain objects to TestCase instances
      const instances = testCases.map(data => new TestCase(data));
      
      // Sort by the specified field
      instances.sort((a, b) => {
        const aVal = a[orderBy.replace('-', '')];
        const bVal = b[orderBy.replace('-', '')];
        
        if (orderBy.startsWith('-')) {
          return new Date(bVal) - new Date(aVal); // Descending
        }
        return new Date(aVal) - new Date(bVal); // Ascending
      });
      
      return instances;
    } catch (error) {
      console.error('Error loading test cases:', error);
      return [];
    }
  }

  /**
   * Find a test case by ID
   * @param {string} id - Test case ID
   * @returns {Promise<TestCase|null>} - Found test case or null
   */
  static async findById(id) {
    const testCases = await TestCase.list();
    return testCases.find(tc => tc.id === id) || null;
  }

  /**
   * Update a test case
   * @param {string} id - Test case ID
   * @param {Object} updates - Data to update
   * @returns {Promise<TestCase|null>} - Updated test case or null
   */
  static async update(id, updates) {
    const testCases = await TestCase.list();
    const index = testCases.findIndex(tc => tc.id === id);
    
    if (index === -1) return null;
    
    testCases[index] = new TestCase({
      ...testCases[index],
      ...updates,
      updated_date: new Date().toISOString()
    });
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testCases));
    return testCases[index];
  }

  /**
   * Delete a test case
   * @param {string} id - Test case ID
   * @returns {Promise<boolean>} - Success status
   */
  static async delete(id) {
    const testCases = await TestCase.list();
    const filtered = testCases.filter(tc => tc.id !== id);
    
    if (filtered.length === testCases.length) return false;
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
  }

  /**
   * Save this test case instance
   * @returns {Promise<TestCase>} - Saved test case
   */
  async save() {
    this.updated_date = new Date().toISOString();
    
    const testCases = await TestCase.list();
    const index = testCases.findIndex(tc => tc.id === this.id);
    
    if (index >= 0) {
      testCases[index] = this;
    } else {
      testCases.push(this);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testCases));
    return this;
  }

  /**
   * Convert to JSON representation
   * @returns {Object} - JSON object
   */
  toJSON() {
    return {
      id: this.id,
      title: this.title,
      user_input: this.user_input,
      expected_response_answer: this.expected_response_answer,
      expected_response_question: this.expected_response_question,
      actual_response_answer: this.actual_response_answer,
      actual_response_question: this.actual_response_question,
      config_data: this.config_data,
      preset_type: this.preset_type,
      environment: this.environment,
      created_date: this.created_date,
      updated_date: this.updated_date
    };
  }
}
